// frontend/src/data/helpContent.js
// All Help Centre article content organised by module

export const helpCategories = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: '🚀',
    articles: [
      {
        id: 'overview',
        title: 'What is DifferentialPay?',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## What is DifferentialPay?

DifferentialPay is Differential Capital's internal payment management system. It provides a controlled, auditable workflow for managing supplier payments from onboarding through to proof of payment reconciliation.

### Core Workflow

1. **Suppliers** are onboarded and approved before any payments can be made to them
2. **Payment Batches** are created grouping multiple supplier payments together
3. **Approval workflows** ensure payments are reviewed before being processed
4. **Payment Schedules** are generated and sent to accountants for bank upload
5. **Proof of Payment** documents are uploaded and reconciled against the schedule

### Your Role

Your access level determines what you can see and do in the system:

| Role | Key Permissions |
|------|----------------|
| **Staff** | Create suppliers, create batches, upload POP |
| **Supplier Approver** | All Staff permissions + approve/reject suppliers |
| **Payment Approver** | All Staff permissions + approve/reject payment batches |
| **Admin** | Full access including settings and user management |
        `
      },
      {
        id: 'navigation',
        title: 'Navigating the System',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## Navigating the System

The sidebar on the left gives you access to all modules. What you see depends on your role.

### Sidebar Sections

- **Dashboard** — Your personalised overview with key metrics and pending actions
- **Suppliers** — Manage the supplier directory
- **Payment Batches** — Create and manage payment batches
- **Reports** — View payment history and export data
- **Settings** — Configure accountant recipients and export settings *(Admin only)*
- **Help** — You're here!

### Dashboard

The dashboard is your home screen. It shows:
- Counts of items requiring your attention (pending approvals, missing POP)
- Recent activity
- Quick links to common actions

### Notifications

The bell icon in the top right shows your unread notifications. You'll receive notifications when:
- A supplier or batch you submitted is approved or rejected
- Action is required from you
        `
      },
    ]
  },
  {
    id: 'suppliers',
    label: 'Suppliers',
    icon: '🏢',
    articles: [
      {
        id: 'supplier-overview',
        title: 'Supplier Directory Overview',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## Supplier Directory

The supplier directory is the master list of all companies that Differential Capital makes payments to. A supplier must be **Approved** before they can be included in a payment batch.

### Supplier Statuses

| Status | Meaning |
|--------|---------|
| **Pending** | Submitted, awaiting review by a Supplier Approver |
| **Approved** | Verified and available for payment batches |
| **Rejected** | Not approved — cannot be used in batches |

### Supplier Information

Each supplier record contains:
- Company details (name, registration number, VAT number)
- Banking details (bank, account number, branch code)
- Service category
- Supporting documents
- Screening checklist
        `
      },
      {
        id: 'add-supplier',
        title: 'Adding a New Supplier',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## Adding a New Supplier

1. Go to **Suppliers** in the sidebar
2. Click **Add Supplier** (top right)
3. Fill in all required fields:
   - Company name
   - Registration number
   - Banking details (bank, account number, branch code, account type)
   - Service category
4. Upload supporting documents (e.g. bank confirmation letter, company registration)
5. Click **Submit**

The supplier will be created with **Pending** status and sent to a Supplier Approver for review.

### Important Notes

- Banking details must be accurate — these are used to generate payment files
- The bank confirmation letter is required to verify account details
- You will receive a notification when your submission is reviewed
        `
      },
      {
        id: 'approve-supplier',
        title: 'Approving or Rejecting a Supplier',
        roles: ['Supplier Approver', 'Admin'],
        content: `
## Approving or Rejecting a Supplier

*(Supplier Approver and Admin only)*

1. Go to **Suppliers** and filter by **Pending** status, or check your Dashboard for pending items
2. Click on the supplier to open their record
3. Review all details including documents and the screening checklist
4. Complete the **Screening Checklist** — all items should be verified
5. Click **Approve** or **Reject**
   - If rejecting, provide a reason — this is sent to the submitter

### Screening Checklist

The checklist ensures due diligence is completed before a supplier is approved. Items include verification of banking details, company registration, and compliance requirements.

### After Approval

Once approved, the supplier becomes available for selection in payment batches. The submitter receives an email notification of the outcome.
        `
      },
    ]
  },
  {
    id: 'batches',
    label: 'Payment Batches',
    icon: '💳',
    articles: [
      {
        id: 'batch-overview',
        title: 'Payment Batches Overview',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## Payment Batches

A payment batch groups multiple supplier payments together for a single payment date. The batch goes through a review and approval workflow before a payment schedule is generated.

### Batch Statuses

| Status | Meaning |
|--------|---------|
| **Draft** | Being built, not yet submitted |
| **Pending Review** | Submitted for approval |
| **In Review** | Being reviewed by a Payment Approver |
| **Approved** | Approved — schedule can be generated |
| **Rejected** | Sent back — requires changes |
| **Paid** | Payment schedule uploaded to bank |

### Batch Reference

Each batch is automatically assigned a reference in the format \`BATCH-YYYYMMDD-HHMMSS\`.
        `
      },
      {
        id: 'create-batch',
        title: 'Creating a Payment Batch',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## Creating a Payment Batch

1. Go to **Payment Batches** and click **New Batch**
2. Set the **Payment Date** — the date payments will be made
3. Add payments using the **Add Payment** button:
   - Select an approved supplier
   - Enter the amount and currency
   - Add a reference/description
   - Assign a cost centre (optional)
4. Review the batch total
5. Click **Submit for Review** when ready

### Notes

- You can save a batch as **Draft** and come back to it later
- Only **Approved** suppliers can be added to a batch
- Multiple currencies are supported — ZAR payments generate the local schedule
- You'll receive a notification when the batch is reviewed
        `
      },
      {
        id: 'approve-batch',
        title: 'Approving or Rejecting a Batch',
        roles: ['Payment Approver', 'Admin'],
        content: `
## Approving or Rejecting a Batch

*(Payment Approver and Admin only)*

1. Go to **Payment Batches** or check your Dashboard for pending batches
2. Click **Start Review** to open the batch
3. Review each payment — supplier, amount, currency, reference
4. Click **Approve** or **Reject**
   - If rejecting, provide a reason for the submitter

### After Approval

Once approved:
- The payment data is **locked** as a point-in-time snapshot (future changes to supplier details won't affect this batch)
- The **Send Schedule** button becomes available to email the payment schedule to accountants
- The batch moves to **Approved** status
        `
      },
      {
        id: 'payment-schedule',
        title: 'Payment Schedule & Bank Upload',
        roles: ['Payment Approver', 'Admin'],
        content: `
## Payment Schedule & Bank Upload

After a batch is approved, a payment schedule Excel file can be generated and emailed to accountants for upload to the bank.

### Sending the Schedule

1. Open an **Approved** batch
2. Click **Send Schedule**
3. The system generates an Excel file with all ZAR payments formatted for bank upload
4. The file is automatically emailed to all active accountant recipients configured in Settings
5. The batch status updates to reflect the schedule has been sent

### The Excel File

The payment schedule file (\`YYYYMMDD_PMT_LOCAL.xlsx\`) contains:
- Supplier name and banking details (captured at approval time)
- Payment amounts
- References

### Accountant Recipients

Accountant email addresses are managed in **Settings → Accountant Recipients**. Recipients can be configured as TO or CC. *(Admin only)*
        `
      },
    ]
  },
  {
    id: 'pop',
    label: 'Proof of Payment',
    icon: '📎',
    articles: [
      {
        id: 'pop-overview',
        title: 'Proof of Payment Overview',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## Proof of Payment (POP)

Once the accountant has uploaded the payment file to the bank, proof of payment (POP) documents need to be uploaded into DifferentialPay against each payment in the schedule.

### Reconciliation

The system automatically reconciles uploaded POP documents against payment records by reading the PDF and matching amounts and references.

### POP Statuses

| Status | Meaning |
|--------|---------|
| **Pending** | No POP uploaded yet |
| **Uploaded** | POP document attached |
| **Reconciled** | Automatically matched to the payment record |
        `
      },
      {
        id: 'upload-pop',
        title: 'Uploading Proof of Payment',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## Uploading Proof of Payment

1. Go to **Payment Batches** and open the relevant batch
2. Navigate to the **Payment Schedule** tab
3. Find the payment record and click **Upload POP**
4. Select the PDF file from your computer
5. The system will attempt to automatically reconcile the document

### File Requirements

- Format: **PDF only**
- The PDF should contain the payment amount and reference for automatic reconciliation
- Encrypted PDFs are supported

### If Reconciliation Fails

If the system can't automatically reconcile the POP, you can manually mark a payment as reconciled after verifying the document manually.

### Bulk Upload

If the bank provides a single POP document covering multiple payments, upload it against the relevant payment record and note this in the reference field.
        `
      },
    ]
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: '📊',
    articles: [
      {
        id: 'reports-overview',
        title: 'Reports Overview',
        roles: ['Staff', 'Supplier Approver', 'Payment Approver', 'Admin'],
        content: `
## Reports

The Reports section provides visibility into payment history and activity across the system.

### Available Reports

- **Payment History** — All payments across batches with filtering by date, supplier, and status
- **Supplier Summary** — Payment totals per supplier
- **Batch Summary** — Overview of all batches and their statuses

### Filtering

Use the filter panel to narrow results by:
- Date range
- Supplier
- Batch status
- Currency
- Cost centre

### Exporting

All reports can be exported to Excel using the **Export** button. The export respects your current filters.

### Date Range

By default reports show a rolling date range. For performance, avoid clearing all date filters as this loads the full history.
        `
      },
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: '⚙️',
    articles: [
      {
        id: 'accountant-recipients',
        title: 'Managing Accountant Recipients',
        roles: ['Admin'],
        content: `
## Accountant Recipients

*(Admin only)*

Accountant recipients are the email addresses that receive the payment schedule Excel file when it is sent from an approved batch.

### Adding a Recipient

1. Go to **Settings → Accountant Recipients**
2. Click **Add Recipient**
3. Enter the name and email address
4. Set the recipient type:
   - **TO** — Primary recipient (at least one required)
   - **CC** — Carbon copy recipient
5. Ensure **Active** is checked
6. Click **Save**

### Deactivating a Recipient

Toggle the **Active** switch to deactivate a recipient without deleting them. Inactive recipients won't receive schedule emails.

### Security Note

Payment schedules are sent directly to accountants' email addresses to prevent users from intercepting and modifying banking details before the file reaches the bank.
        `
      },
      {
        id: 'export-config',
        title: 'Payment Export Configuration',
        roles: ['Admin'],
        content: `
## Payment Export Configuration

*(Admin only)*

The Payment Export Config record controls how payment batch Excel files are formatted when exported.

This record is managed via the **Django Admin** panel at \`/admin\`.

A PaymentExportConfig record must exist for the batch finish review workflow to function. If you receive an error during batch approval related to export configuration, check that this record exists in the admin panel.

Contact your system administrator if you need to modify export settings.
        `
      },
    ]
  },
];

// Flatten all articles for search
export const getAllArticles = () => {
  return helpCategories.flatMap(cat =>
    cat.articles.map(article => ({
      ...article,
      categoryId: cat.id,
      categoryLabel: cat.label,
      categoryIcon: cat.icon,
    }))
  );
};